import { useEffect, useId, useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Check, Loader2, Search } from 'lucide-react';
import type { PersonRef } from '@central/contracts';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/features/auth/auth.store';
import {
  buscarPessoas,
  chavesDeApoio,
  MAXIMO_DE_IDS_POR_CHAMADA,
  resolverPessoas,
} from '@/features/tickets/lookups.api';

/**
 * Identificador de pessoa → nome, nas telas de chamado.
 *
 * O cache é **por pessoa** e a requisição é **por tela**. As duas coisas juntas
 * são o ponto deste arquivo:
 *
 *   • se a chave do cache fosse o lote, a mesma pessoa vista na lista e no
 *     detalhe seria buscada de novo, porque o conjunto de ids é outro;
 *   • se cada `useQuery` por id fosse direto à rede, uma tabela de 25 linhas
 *     faria 25 requisições — o N+1 mudado de lugar, do banco para o navegador.
 *
 * A junção acontece abaixo do TanStack Query: cada id vira uma consulta com
 * chave própria, mas a função de busca deposita o id num lote que só é enviado
 * no fim do turno atual. Todos os ids pedidos pela mesma renderização saem numa
 * requisição só, e cada um volta para sua própria entrada de cache.
 */

const CINCO_MINUTOS = 5 * 60_000;

// ── junção das buscas ──────────────────────────────────────────────────────

let idsPendentes = new Set<string>();
let loteEmCurso: Promise<Map<string, PersonRef>> | null = null;

/**
 * Enfileira o id e devolve o que o lote encontrar para ele.
 *
 * O `Promise.resolve().then()` adia o envio para o fim do turno de JavaScript
 * corrente. É tempo suficiente: o React monta a tela inteira e o TanStack
 * dispara todas as consultas antes de a fila de microtarefas rodar.
 */
function resolverEmLote(id: string): Promise<PersonRef | null> {
  idsPendentes.add(id);

  let lote = loteEmCurso;
  if (!lote) {
    lote = Promise.resolve().then(() => {
      const ids = [...idsPendentes];
      // Zerados antes do envio: id pedido daqui em diante pertence ao próximo
      // lote, e não pode ser engolido por uma requisição que já partiu.
      idsPendentes = new Set();
      loteEmCurso = null;
      return buscarLote(ids);
    });
    loteEmCurso = lote;
  }

  return lote.then((encontrados) => encontrados.get(id) ?? null);
}

async function buscarLote(ids: string[]): Promise<Map<string, PersonRef>> {
  const encontrados = new Map<string, PersonRef>();
  // Fatiado no teto do DTO: acima dele a resposta é 400, não um resultado
  // truncado.
  for (let inicio = 0; inicio < ids.length; inicio += MAXIMO_DE_IDS_POR_CHAMADA) {
    const fatia = ids.slice(inicio, inicio + MAXIMO_DE_IDS_POR_CHAMADA);
    for (const pessoa of await resolverPessoas(fatia)) encontrados.set(pessoa.id, pessoa);
  }
  return encontrados;
}

// ── dicionário ─────────────────────────────────────────────────────────────

export interface DicionarioDePessoas {
  /**
   * `undefined` enquanto a resolução está em curso, `null` quando o id não
   * resolveu — pessoa de outro tenant, apagada, ou a consulta falhou.
   *
   * Os três casos são distintos na tela: "carregando" não pode parecer
   * "desconhecido", e nenhum dos dois pode virar um nome inventado.
   */
  pessoa: (id: string | null | undefined) => PersonRef | null | undefined;
}

/**
 * Resolve todos os ids de pessoa de uma tela.
 *
 * Recebe a lista inteira — com nulos e repetições, como vem dos dados — e cuida
 * de reduzi-la: repetição vira uma consulta só, e a ordem estável evita que a
 * mesma tela reordene a lista de consultas a cada renderização.
 */
export function useDicionarioDePessoas(
  ids: ReadonlyArray<string | null | undefined>,
): DicionarioDePessoas {
  const assinatura = ids.filter(Boolean).join(',');
  const unicos = useMemo(
    () => [...new Set(assinatura.split(',').filter(Boolean))].sort(),
    [assinatura],
  );

  return useQueries({
    queries: unicos.map((id) => ({
      queryKey: chavesDeApoio.pessoa(id),
      queryFn: () => resolverEmLote(id),
      staleTime: CINCO_MINUTOS,
    })),
    combine: (resultados) => {
      const mapa = new Map<string, PersonRef | null>();
      resultados.forEach((resultado, indice) => {
        const id = unicos[indice];
        if (!id) return;
        if (resultado.isSuccess) mapa.set(id, resultado.data);
        // Falha na resolução é "não sei quem é", não "ainda estou buscando":
        // deixar como carregando manteria a tela num spinner permanente.
        else if (resultado.isError) mapa.set(id, null);
      });
      return { pessoa: (id: string | null | undefined) => (id ? mapa.get(id) : null) };
    },
  });
}

// ── exibição ───────────────────────────────────────────────────────────────

export interface NomeDePessoaProps {
  id: string | null | undefined;
  pessoas: DicionarioDePessoas;
  /** O que dizer quando não há id — "não atribuído", "—". */
  semId?: string;
  className?: string;
}

/**
 * Nome de uma pessoa, com os quatro estados possíveis à mostra.
 *
 * Sem id, carregando, resolvido e não resolvido dizem coisas diferentes, e o
 * último é o que mais importa: um id que não resolve continua sendo um dado do
 * chamado, então a tela admite que não sabe de quem é e guarda o identificador
 * no `title` — quem for investigar precisa dele.
 */
export function NomeDePessoa({ id, pessoas, semId = '—', className }: NomeDePessoaProps) {
  const usuario = useAuthStore((estado) => estado.user);

  if (!id) {
    return <span className={cn('italic text-muted-foreground', className)}>{semId}</span>;
  }

  // O próprio nome do operador não precisa de ida à rede — e "Você" é mais
  // rápido de ler numa fila do que o nome dele repetido em todas as linhas.
  if (id === usuario?.id) {
    return <span className={cn('font-medium', className)}>Você</span>;
  }

  const pessoa = pessoas.pessoa(id);

  if (pessoa === undefined) {
    return (
      <span className={cn('text-muted-foreground', className)} aria-busy>
        carregando…
      </span>
    );
  }

  if (pessoa === null) {
    return (
      <span
        className={cn('italic text-muted-foreground', className)}
        title={`Identificador ${id} — sem cadastro correspondente`}
      >
        pessoa não identificada
      </span>
    );
  }

  return (
    <span className={className} title={pessoa.email}>
      {pessoa.name}
      {pessoa.active ? null : (
        <span className="ml-1 text-xs text-muted-foreground">(inativa)</span>
      )}
    </span>
  );
}

// ── busca ──────────────────────────────────────────────────────────────────

const ESPERA_DA_BUSCA = 300;
const MINIMO_DE_LETRAS = 2;

export interface SeletorDePessoaProps {
  /** Pessoa já escolhida, para o campo mostrar de quem se trata. */
  escolhida?: PersonRef | null;
  aoEscolher: (pessoa: PersonRef) => void;
  rotulo: string;
  placeholder?: string;
  desabilitado?: boolean;
  /**
   * Função de busca customizada. Padrão: busca pessoas internas.
   * Passe `buscarContatos` (com organizationId fechado em closure) para
   * buscar contatos externos no campo de solicitante.
   */
  buscadorFn?: (termo: string) => Promise<PersonRef[]>;
  /** Chave de cache do TanStack Query. Deve mudar quando o buscador muda. */
  chaveDeBusca?: readonly unknown[];
}

/**
 * Campo de busca de pessoa.
 *
 * Digitar espera 300 ms antes de consultar: sem isso, "Fernanda" seriam oito
 * requisições, sete delas já obsoletas quando respondessem. O mínimo de duas
 * letras é do servidor — com uma, a rota devolveria o cadastro inteiro, e é
 * exatamente a listagem que ela recusa fazer.
 *
 * Aceita um `buscadorFn` para trocar a fonte sem duplicar o componente: o campo
 * de atribuição busca operadores internos; o de solicitante busca contatos
 * externos. A diferença é a função, o comportamento visual é idêntico.
 */
export function SeletorDePessoa({
  escolhida,
  aoEscolher,
  rotulo,
  placeholder = 'Nome ou e-mail',
  desabilitado = false,
  buscadorFn = buscarPessoas,
  chaveDeBusca,
}: SeletorDePessoaProps) {
  const [texto, setTexto] = useState('');
  const [termo, setTermo] = useState('');
  // `useId` porque a tela pode ter mais de um campo destes; `id` fixo faria o
  // rótulo de um apontar para o campo do outro.
  const idDoCampo = useId();
  const listaId = `${idDoCampo}-resultados`;

  useEffect(() => {
    const relogio = setTimeout(() => setTermo(texto.trim()), ESPERA_DA_BUSCA);
    return () => clearTimeout(relogio);
  }, [texto]);

  const busca = useQuery({
    queryKey: chaveDeBusca ? [...chaveDeBusca, termo] : chavesDeApoio.buscaDePessoas(termo),
    queryFn: () => buscadorFn(termo),
    enabled: termo.length >= MINIMO_DE_LETRAS,
    staleTime: CINCO_MINUTOS,
  });

  const resultados = termo.length >= MINIMO_DE_LETRAS ? (busca.data ?? []) : [];

  return (
    <div className="space-y-1.5">
      <label htmlFor={idDoCampo} className="text-sm font-medium">
        {rotulo}
      </label>

      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <input
          id={idDoCampo}
          value={texto}
          disabled={desabilitado}
          onChange={(evento) => setTexto(evento.target.value)}
          placeholder={placeholder}
          role="combobox"
          aria-expanded={resultados.length > 0}
          aria-controls={listaId}
          aria-autocomplete="list"
          className="w-full rounded-lg border border-input/80 bg-muted/40 py-2 pl-9 pr-9 text-sm transition-colors placeholder:text-muted-foreground hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow disabled:opacity-50"
        />
        {busca.isFetching ? (
          <Loader2
            className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
            aria-hidden
          />
        ) : null}
      </div>

      {texto.trim().length > 0 && texto.trim().length < MINIMO_DE_LETRAS ? (
        <p className="text-xs text-muted-foreground">Digite pelo menos duas letras.</p>
      ) : null}

      {busca.isError ? (
        <p role="alert" className="text-xs text-destructive">
          Não foi possível buscar agora.
        </p>
      ) : null}

      {termo.length >= MINIMO_DE_LETRAS && !busca.isFetching && resultados.length === 0 ? (
        <p className="text-xs text-muted-foreground">Ninguém encontrado para "{termo}".</p>
      ) : null}

      {resultados.length > 0 ? (
        <ul
          id={listaId}
          role="listbox"
          className="max-h-56 divide-y divide-border overflow-y-auto rounded-md border border-border bg-card"
        >
          {resultados.map((pessoa) => {
            const atual = pessoa.id === escolhida?.id;
            return (
              <li key={pessoa.id} role="option" aria-selected={atual}>
                <button
                  type="button"
                  onClick={() => {
                    aoEscolher(pessoa);
                    setTexto('');
                    setTermo('');
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-accent"
                >
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-medium">{pessoa.name}</span>
                    {/* O e-mail fica visível, e não só no `title`: é o que
                        distingue dois homônimos na hora de escolher. */}
                    <span className="ml-2 text-xs text-muted-foreground">{pessoa.email}</span>
                  </span>
                  {atual ? <Check className="h-4 w-4 flex-none text-primary" aria-hidden /> : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
